<template>
  <div>
    <div
      v-if="loading"
      class="reports-history-table-empty-wrapper"
    >
      <gl-loader progress-spinner />
    </div>
    <div
      v-else
      class="login"
    >
      <div class="login__form-logo">
        <SatoshiEyeLogo
          class="pointer"
          @click="$router.push({ name: 'landing-page' })"
        />
      </div>
      <div
        v-if="isEnterCodeRoute"
        class="login__form"
      >
        <div
          class="login__form-wrapper"
        >
          <h2 class="login__title">
            Check your email
          </h2>
          <div class="login__sub-title">
            We sent a temporary login code to: <span>{{ emailFromUrl }}</span>
          </div>
          <gl-form
            ref="codeForm"
            class="mb-2"
            full-submit
            ok-only
            submit-title="Continue"
            @submit="handleSubmitEnterLoginCode"
          >
            <gl-input
              v-model="loginCode"
              class="login__input"
              is-block-error-style
              name="Code"
              placeholder="Enter login code"
              rules="required"
            />
            <div class="login__resend-code">
              <div class="login__resend-code-text">
                Didn’t receive your code?
              </div>
              <div
                v-if="!resendDisabled"
                class="login__resend-code-link"
                @click="resendCode"
              >
                Resend code
              </div>
              <div
                v-if="resendDisabled"
                class="login__resend-code-timer"
              >
                ({{ resendTimer }}s)
              </div>
            </div>
          </gl-form>
        </div>
      </div>

      <div
        v-else-if="isUserAgreementRoute"
        class="login__form"
      >
        <div class="login__form-wrapper login__form-wrapper--agreement">
          <h2 class="login__title">
            User Agreement
          </h2>
          <div class="login__sub-title">
            To get started, please review and accept our legal terms.
          </div>
          <form @submit.prevent="handleSubmitUserAgreement">
            <div class="login__agreement-item mb-2">
              <GlCheckbox v-model="isMandatoryChecked" />
              <div
                class="login__agreement-text"
                v-html="mandatoryAgreementHtml"
              />
            </div>

            <div class="login__agreement-item login__agreement-item--age mb-2">
              <GlCheckbox v-model="isAgeConfirmedChecked" />
              <div class="login__agreement-text">
                I confirm that I am of legal age to use the Services and provide personal data independently.
              </div>
            </div>

            <div class="login__agreement-item mb-4">
              <GlCheckbox v-model="isMarketingChecked" />
              <div class="login__agreement-text">
                I agree to receive marketing emails and special offers (Optional).
              </div>
            </div>

            <gl-form
              ref="agreement-form"
              class="mb-2"
              full-submit
              :loading="loading"
              ok-only
              :side-valid="!isMandatoryChecked || !isAgeConfirmedChecked"
              submit-title="Agree and Continue"
              @submit="handleSubmitUserAgreement"
            />
          </form>
        </div>
      </div>
      <div
        v-else
        class="login__form"
      >
        <div
          class="login__form-wrapper"
        >
          <h2 class="login__title">
            Welcome to SatoshiEye
          </h2>
          <div class="login__sub-title">
            Join SatoshiEye and track cryptocurrency transactions with precision down to the satoshi.
          </div>
          <div
            v-if="getGoogleLoginFeatureFlag"
            class="login__google"
          >
            <GoogleLogin
              @googleAuth="sendGoogleToken"
            />
            <div class="login__google-fake-button">
              <GoogleLogo />
              <span>Continue with Google</span>
            </div>
          </div>
          <div class="login__form-or">
            <span>OR</span>
          </div>
          <gl-form
            ref="form"
            class="mb-2"
            full-submit
            ok-only
            submit-title="Continue"
            @submit="handleEnterEmail"
          >
            <gl-input
              v-model="email"
              autocomplete="email"
              class="login__input"
              is-block-error-style
              name="Email"
              placeholder="Enter email address"
              rules="required|email"
            />
          </gl-form>
        </div>
      </div>
      <div class="login__bottom">
        SATOSHI EYE © {{ getFullYear }} ALL RIGHTS RESERVED
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
// Components
import SatoshiEyeLogo from '@/assets/svg/header/SatoshiEyeLogoOnly.svg?inline'
import GoogleLogo from '@/assets/svg/google/GoogleLogo.svg?inline'
import GlForm from '@/components/gl-form'
import GlInput from '@/components/gl-input'
import GoogleLogin from './components/GoogleLogin'
import GlLoader from '@/components/gl-loader'
import GlButton from '@/components/gl-button'
import GlCheckbox from '@/components/gl-checkbox'

// utils
import { contactSatoshieyeSupport } from '@/utils/go-to-route'
// services
import { syncOnboardingState } from '@/services/onboarding-tour'

export default {
  components: {
    GlForm,
    GlInput,
    SatoshiEyeLogo,
    GoogleLogin,
    GoogleLogo,
    GlLoader,
    GlButton,
    GlCheckbox,
  },
  data() {
    return {
      email: '',
      loginCode: '',
      emailFromUrl: '',
      codeFromUrl: '',
      isSubmittingCode: false,
      loading: false,
      resendDisabled: false,
      resendTimer: 0,
      resendInterval: null,
      isMandatoryChecked: false,
      isMarketingChecked: false,
      isAgeConfirmedChecked: false,
    }
  },
  computed: {
    ...mapGetters('analytics', ['getGoogleLoginFeatureFlag']),
    ...mapGetters('termsOfUse', ['getCurrentTermsVersion']),
    ...mapGetters('user', ['isLogin', 'getOnboardingParams', 'isUserAgree', 'isAgeConfirmed']),
    getFullYear() {
      return new Date().getFullYear()
    },
    isEnterCodeRoute() {
      return this.$route.name === 'enter-code'
    },
    isUserAgreementRoute() {
      return this.$route.name === 'user-agreement'
    },
    mandatoryAgreementHtml() {
      const termsUrl = this.$router.resolve({ name: `terms-of-use-${this.getCurrentTermsVersion}` }).href;
      const privacyUrl = this.$router.resolve({ name: 'privacy-policy' }).href;
      const cookieUrl = this.$router.resolve({ name: 'cookie-policy' }).href;

      return `I have read and agree to the <a href="${termsUrl}" target="_blank" class="login__form-terms-link">Terms of Use</a>, <a href="${privacyUrl}" target="_blank" class="login__form-terms-link">Privacy Policy</a>, and <a href="${cookieUrl}" target="_blank" class="login__form-terms-link">Cookie Policy</a>.`;
    }
  },
  watch: {
    $route: {
      immediate: true,
      handler(newRoute) {
        if (newRoute.name !== 'enter-code') return

        const email = newRoute.query.email
        const code = newRoute.query.code

        if (this.emailFromUrl === email && this.codeFromUrl === code) return

        this.emailFromUrl = email
        this.codeFromUrl = code

        if (!email) {
          this.$router.replace({ name: 'login' })
        } else if (code) {
          this.handleSubmitEnterLoginCode()
        }
      },
    },
  },
  beforeDestroy() {
    clearInterval(this.resendInterval)
  },
  mounted() {
    const lastSent = Number(localStorage.getItem('otpLastSentAt') || 0)
    const diff = Math.floor((Date.now() - lastSent) / 1000)
    if (diff < 60) {
      this.resendTimer = 60 - diff
      this.resendDisabled = true
      this.initResendTimer()
    }
  },
  methods: {
    ...mapMutations({
      SET_USER_TOKEN: 'user/SET_USER_TOKEN',
      SET_USER_ID: 'user/SET_USER_ID',
      REMOVE_USER_TOKEN: 'user/REMOVE_USER_TOKEN',
      SET_USER_REFRESH_TOKEN: 'user/SET_USER_REFRESH_TOKEN',
      REMOVE_USER_ID: 'user/REMOVE_USER_ID',
    }),
    ...mapActions('user', ['googleAuth', 'getMe', 'requestOtp', 'verifyOtp', 'acceptTerms']),
    contactSatoshieyeSupport,
    syncOnboardingState,
    async handleEnterEmail() {
      const email = this.emailFromUrl || (this.email || '').toLowerCase()
      this.loading = true
      await this.clearAuthData()
      await this.requestOtp({ email })
          .then(({ success }) => {
            if (success && !this.isEnterCodeRoute) {
              localStorage.setItem('otpLastSentAt', Date.now())
              this.$router.push({ name: 'enter-code', query: { email: this.email.toLowerCase() } })
            } else if (success && this.isEnterCodeRoute) {
              localStorage.setItem('otpLastSentAt', Date.now())
              this.initResendTimer()
            }
          })
          .catch(() => {
            localStorage.setItem('otpLastSentAt', '')
          })
          .finally(() => {
            this.loading = false
          })
    },
    async handleSubmitEnterLoginCode() {
      if (this.isSubmittingCode) return
      this.loading = true
      this.isSubmittingCode = true

      const email = this.emailFromUrl || (this.email || '').toLowerCase()
      const code = this.codeFromUrl || this.loginCode

      await this.clearAuthData()
      await this.verifyOtp({ email, code })
          .then(async ({ data }) => {
            this.SET_USER_TOKEN(data)
            this.SET_USER_REFRESH_TOKEN(data)
            this.SET_USER_ID(data)
            await this.getMe()

            if (!this.isUserAgree || !this.isAgeConfirmed) {
              await this.$router.push({ name: 'user-agreement' })
              return
            }

            await this.syncOnboardingState({ remote: this.getOnboardingParams })

            const to = localStorage.getItem('to.fullPath')
            const promo = localStorage.getItem('promocode')
            if (to) {
              this.$router.push(to)
            } else if (promo) {
              this.$router.push({ name: 'payment' })
            } else {
              this.$router.push({ name: 'source-of-funds' })
            }
          })
          .catch(({ response: { data } }) => {
            this.$toast.add({
              severity: 'error',
              summary: 'Error Message',
              detail: this.handleErrorMessage(data.data.message),
              life: 3000,
            })
          })
          .finally(() => {
            this.loading = false
            this.isSubmittingCode = false
          })
    },
    async handleSubmitUserAgreement() {
      if (!this.isMandatoryChecked || !this.isAgeConfirmedChecked) return

      this.loading = true
      const version = `${window.location.origin}/terms-of-use-${this.getCurrentTermsVersion}`

      await this.acceptTerms({
        version,
        authToken: this.isLogin,
        ageConfirmed: this.isAgeConfirmedChecked,
        marketingConsent: this.isMarketingChecked
      })
          .then(async ({ data, success }) => {
            if (success) {
              await this.getMe()

              const to = localStorage.getItem('to.fullPath')
              const promo = localStorage.getItem('promocode')
              if (to && to !== '/user-agreement') {
                this.$router.push(to)
              } else if (promo) {
                this.$router.push({ name: 'payment' })
              } else {
                this.$router.push({ name: 'source-of-funds' })
              }
            } else {
              this.$toast.add({
                severity: 'error',
                summary: 'Error Message',
                detail: data.message,
                life: 3000,
              })
            }
          })
          .catch(({ response: { data } }) => {
            this.$toast.add({
              severity: 'error',
              summary: 'Error Message',
              detail: data.data.message,
              life: 3000,
            })
          })
          .finally(() => {
            this.loading = false
          })
    },
    clearAuthData() {
      this.REMOVE_USER_TOKEN()
      this.REMOVE_USER_ID()
    },
    contactSupport() {
      this.contactSatoshieyeSupport()
    },
    async sendGoogleToken(token) {
      await this.clearAuthData()
      await this.googleAuth({ token })
          .then(async ({ data, success }) => {
            if (success) {
              this.SET_USER_TOKEN(data)
              this.SET_USER_REFRESH_TOKEN(data)
              this.SET_USER_ID(data)
              await this.getMe()
              await this.syncOnboardingState({ remote: this.getOnboardingParams })
              if (localStorage.getItem('to.fullPath')) {
                this.$router.push(localStorage.getItem('to.fullPath'))
              } else if(localStorage.getItem('promocode')) {
                this.$router.push({ name: 'payment' })
              } else {
                this.$router.push({ name: 'source-of-funds' })
              }
            } else {
              this.$refs.form.setErrors({
                Email: data.message,
              })
            }
          })
          .catch(({ response: { data } }) => {
            this.$refs.form.setErrors({
              Email: data.data.message,
            })
          })
    },
    handleErrorMessage(message) {
      if (message === 'INCORRECT_INPUT_DATA') {
        return 'The OTP you entered is incorrect. Please try again.'
      }
      if (message === 'INVALID OTP') {
        return 'This OTP is invalid or has already been used. Please request a new one.'
      }
      if (message === 'OTP EXPIRED') {
        return 'The OTP has expired. Please request a new one.'
      }
      return message
    },
    initResendTimer() {
      const lastSent = Number(localStorage.getItem('otpLastSentAt') || 0)
      const diff = Math.floor((Date.now() - lastSent) / 1000)
      this.resendTimer = Math.max(60 - diff, 0)
      this.resendDisabled = this.resendTimer > 0

      if (this.resendTimer > 0) {
        this.resendInterval = setInterval(() => {
          if (this.resendTimer > 0) {
            this.resendTimer--
          }
          if (this.resendTimer <= 0) {
            this.resendDisabled = false
            clearInterval(this.resendInterval)
          }
        }, 1000)
      }
    },
    async resendCode() {
      if (this.resendDisabled) return

      await this.handleEnterEmail()

      localStorage.setItem('otpLastSentAt', Date.now())
      this.initResendTimer()
    },
  },
}
</script>

<style>
/* ... (Твои текущие базовые стили .login, .login__title и т.д. остаются нетронутыми) ... */
.login {
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100dvh;
  padding-top: 60px;
  padding-bottom: 24px;
  overflow: auto;
}

.login__form {
  flex-grow: 1;
  display: flex;
  padding-top: 48px;
  padding-bottom: 24px;
}
.login__form-logo {
  display: flex;
  justify-content: center;
  align-items: center;
}
.login__form-wrapper {
  width: 378px;
  max-width: 378px;
}

.login__title {
  margin-bottom: 16px;
  font-weight: 700;
  font-size: 24px;
  line-height: 32px;
  color: var(--Texts-General-Primary);
  text-align: center;
}
.login__sub-title {
  margin-bottom: 24px;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  text-align: center;
  color: var(--Texts-General-Secondary);
}
.login__form-terms-link {
  color: var(--Texts-General-Action);
  text-decoration: none;
}

.login__agreement-item {
  display: flex;
  height: 64px;
  padding: 12px;
  align-items: center;
  border-radius: 12px;
  border: 1px solid var(--Basic-200, rgba(9, 21, 64, 0.08));
}
.login__agreement-item--age {
  height: auto;
  min-height: 64px;
}

.login__agreement-text {
  color: var(--Basic-900, #091540);
  font-size: var(--Size-M, 14px);
  font-style: normal;
  font-weight: 500;
  line-height: var(--Line-Height-L, 20px);
  letter-spacing: var(--Letter-Spacing-S, -0.2px);
}

.login__input {
  margin-bottom: 24px;
}
.login__input input {
  height: 44px !important;
  background: var(--Inputs-BG-Secondary);
  border: 1px solid var(--Inputs-Border-Secondary);
  border-radius: var(--Corner-radius-S);
}
.login__input input::placeholder {
  color: var(--Texts-General-Disabled);
}
.login__input .gl-input__error--block {
  margin-top: 8px;
}
.full-submit {
  width: 100%;
  height: 44px !important;
}

.login__google {
  height: 44px;
  border-radius: var(--Corner-radius-S);
  position: relative;
  overflow: hidden;
}
.login__google-fake-button {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  width: 100%;
  height: 100%;
  background-color: #fff;
  border: 1px solid var(--Inputs-Border-Secondary);
  border-radius: var(--Corner-radius-S);
  position: absolute;
  left: 0;
  top: 0;
  pointer-events: none;
}
.login__form-or {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 24px;
  margin: 24px 0;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  color: var(--Texts-General-Secondary);
}
.login__form-or:before,
.login__form-or:after {
  content: '';
  flex: 1;
  height: 1px;
  background-color: var(--Inputs-Border-Secondary);
}

.login__bottom {
  font-weight: 500;
  font-size: 12px;
  color: var(--Texts-General-Secondary);
  text-align: center;
}

.gl-loader-spiner {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100dvh;
}

.login__resend-code {
  display: flex;
  gap: 4px;
  margin-bottom: 24px;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
}
.login__resend-code-text {
  color: var(--Basic-700);
}
.login__resend-code-link {
  color: var(--Texts-General-Action);
  cursor: pointer;
}
.login__resend-code-timer {
  color: var(--Texts-General-Action);
}

@media screen and (max-width: 1023px) {

}

@media screen and (max-width: 440px) {
  .login__form-wrapper {
    width: 100dvw;
    max-width: 100dvw;
    padding-left: 16px;
    padding-right: 16px;
  }
  .login__google {
    max-width: 100%;
    width: 100%;
  }
}
</style>
